import React from 'react';
import EditableText, { EditableButton } from './EditableText';

/**
 * EditableHeroSection - Hero section with inline editing support
 *
 * Props:
 * - headline: Main headline text
 * - subheadline: Secondary text
 * - ctaText: Button text
 * - branding: Color/font configuration
 * - isEditing: Enable/disable edit mode
 * - onUpdate: Callback for content changes
 */
export default function EditableHeroSection({
  headline = 'Your Headline Here',
  subheadline = 'Your subheadline goes here. Describe the value proposition.',
  ctaText = 'Get Started',
  badge = '✓ IRS-Compliant Strategies',
  stats = [
    { value: '$50M+', label: 'Total Savings Delivered' },
    { value: '1,200+', label: 'Clients Protected' },
    { value: '98%', label: 'Client Satisfaction Rate' }
  ],
  trustBadges = ['Zero Audit Risk', 'CPA Approved', '100% Compliant'],
  testimonial = {
    quote: '"Saved us $200K in two years. Zero audit risk."',
    author: 'Jennifer Torres, CFO'
  },
  branding = {},
  isEditing = false,
  onUpdate,
  onCtaClick
}) {
  const primaryColor = branding?.primary_color || '#1e40af';
  const accentColor = branding?.secondary_color || '#AEFFE3';

  const handleUpdate = (key, value) => {
    if (onUpdate) {
      onUpdate({ [key]: value });
    }
  };

  const handleStatUpdate = (index, key, value) => {
    if (onUpdate) {
      const newStats = [...stats];
      newStats[index] = { ...newStats[index], [key]: value };
      onUpdate({ stats: newStats });
    }
  };

  const handleTestimonialUpdate = (key, value) => {
    if (onUpdate) {
      onUpdate({
        testimonial: { ...testimonial, [key]: value }
      });
    }
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 py-20 px-6 overflow-hidden flex items-center">
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 -right-40 w-96 h-96 rounded-full blur-3xl opacity-30 animate-pulse"
          style={{ backgroundColor: accentColor, animationDuration: '4s' }}
        />
        <div
          className="absolute bottom-0 -left-40 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse"
          style={{ backgroundColor: primaryColor, animationDuration: '6s' }}
        />
        <div
          className="absolute top-1/2 left-1/2 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ backgroundColor: accentColor }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10 w-full">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Main content */}
          <div>
            {/* Badge */}
            <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
              <EditableText
                value={badge}
                onChange={(text) => handleUpdate('badge', text)}
                isEditing={isEditing}
                element="span"
                className="text-sm font-semibold"
                style={{ color: accentColor }}
              />
            </div>

            {/* Headline */}
            <EditableText
              value={headline}
              onChange={(text) => handleUpdate('headline', text)}
              isEditing={isEditing}
              element="h1"
              className="text-5xl md:text-7xl font-bold leading-tight mb-6 text-white"
              style={{ fontFamily: branding?.fonts?.heading || 'Inter' }}
              multiline
            />

            {/* Subheadline */}
            <EditableText
              value={subheadline}
              onChange={(text) => handleUpdate('subheadline', text)}
              isEditing={isEditing}
              element="p"
              className="text-xl text-blue-100 mb-8 leading-relaxed"
              multiline
            />

            {/* CTA Button */}
            <EditableButton
              value={ctaText}
              onChange={(text) => handleUpdate('ctaText', text)}
              isEditing={isEditing}
              onClick={onCtaClick}
              className="group px-8 py-4 rounded-xl font-semibold text-lg text-white hover:scale-105 transition-all transform shadow-2xl relative overflow-hidden"
              style={{ backgroundColor: primaryColor }}
            />

            {/* Trust badges */}
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-blue-200">
              {trustBadges.map((badge, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {isEditing ? (
                    <EditableText
                      value={badge}
                      onChange={(text) => {
                        const newBadges = [...trustBadges];
                        newBadges[idx] = text;
                        handleUpdate('trustBadges', newBadges);
                      }}
                      isEditing={isEditing}
                      element="span"
                    />
                  ) : (
                    <span>{badge}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Glass morphism stats card */}
          <div className="hidden md:block">
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="space-y-6">
                {stats.map((stat, idx) => (
                  <div key={idx} className={idx < stats.length - 1 ? 'pb-6 border-b border-white/20' : ''}>
                    <EditableText
                      value={stat.value}
                      onChange={(text) => handleStatUpdate(idx, 'value', text)}
                      isEditing={isEditing}
                      element="div"
                      className="text-5xl font-bold mb-2"
                      style={{ color: accentColor }}
                    />
                    <EditableText
                      value={stat.label}
                      onChange={(text) => handleStatUpdate(idx, 'label', text)}
                      isEditing={isEditing}
                      element="div"
                      className="text-white"
                    />
                  </div>
                ))}
              </div>

              {/* Mini testimonial */}
              {testimonial && (
                <div className="mt-8 pt-8 border-t border-white/20">
                  <div className="flex items-start gap-3">
                    <svg className="w-8 h-8 text-white/40 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                    </svg>
                    <div>
                      <EditableText
                        value={testimonial.quote}
                        onChange={(text) => handleTestimonialUpdate('quote', text)}
                        isEditing={isEditing}
                        element="p"
                        className="text-white/90 italic text-sm mb-2"
                      />
                      <EditableText
                        value={testimonial.author}
                        onChange={(text) => handleTestimonialUpdate('author', text)}
                        isEditing={isEditing}
                        element="div"
                        className="text-xs text-white/60"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit mode overlay indicator */}
      {isEditing && (
        <div className="absolute top-4 left-4 px-3 py-1 bg-blue-500/80 text-white text-xs font-medium rounded-full backdrop-blur-sm">
          Hero Section
        </div>
      )}
    </section>
  );
}
